import { toast } from '@ahm/react-ui/components/toast';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type PropsWithChildren,
} from 'react';
import { useForgotPasscodeMutation } from '../api';
import { AuthStep, AuthTab, PUBLIC_PATHS } from '../lib/constants';
import { useCustomRouter, usePathname } from '../lib/navigation';
import type { Locales } from '../locales';
import { ROUTES } from '../router';
import { useAuthStore } from '../store/auth-store-provider';
import { BannedModal } from './banned-modal';
import ConfirmResetPasscodeModal from './confirm-reset-passcode-modal';
import ForgotPasscodeModal from './forgot-passcode-modal';
import UserNotFoundModal from './user-not-found-modal';

type AuthModalStateBase = { opened: boolean };

type AuthModals = {
  confirmResetPasscode: AuthModalStateBase & {
    mobile?: string;
    lang?: keyof Locales;
  };
  forgotPasscode: AuthModalStateBase & {
    mobile?: string;
    asPage?: boolean;
    initialData?: any;
    lang?: keyof Locales;
  };
  banned: AuthModalStateBase & {
    type?: AuthStep;
    content?: string;
    countdownInSecond?: number;
    confirmLabel?: string;
    title?: string;
    lang?: keyof Locales;
  };
  userNotFound: AuthModalStateBase & { mobile?: string; lang?: keyof Locales };
};

export type AuthModalName = keyof AuthModals;

type AuthModalContextType = {
  openAuthModal: <T extends AuthModalName>(
    modalName: T,
    modalData: Partial<AuthModals[T]>
  ) => void;
  closeAuthModal: (modalName: AuthModalName) => void;
};

const AuthModalContext = createContext<AuthModalContextType | undefined>(
  undefined
);

export const AuthModalProvider: React.FC<
  PropsWithChildren<{
    isMobile?: boolean;
    lang?: keyof Locales;
  }>
  // eslint-disable-next-line sonarjs/cognitive-complexity
> = ({ children, lang = 'vi', isMobile = false }) => {
  const pathname = usePathname();
  const { navigate } = useCustomRouter();

  const {
    phoneNumber,
    setPhoneNumber,
    setApiError,
    setActiveAuthStep,
    setAuthTabScreen,
  } = useAuthStore((state) => state);

  const [modals, setModals] = useState<AuthModals>({
    confirmResetPasscode: { opened: false },
    forgotPasscode: { opened: false, asPage: false },
    banned: { opened: false, type: AuthStep.OTP },
    userNotFound: { opened: false },
  });

  const openAuthModal = useCallback(
    <T extends AuthModalName>(
      modalName: T,
      modalData: Partial<AuthModals[T]>
    ) => {
      setModals((prev) => ({
        ...prev,
        [modalName]: { ...prev[modalName], ...modalData, opened: true },
      }));
    },
    []
  );

  const closeAuthModal = useCallback((modalName: AuthModalName) => {
    setModals((prev) => ({
      ...prev,
      [modalName]: { ...prev[modalName], opened: false },
    }));
  }, []);

  const { mutate: forgotPassCode } = useForgotPasscodeMutation({
    onSuccess: (data, requestBody) => {
      openAuthModal('forgotPasscode', {
        mobile: requestBody.mobile,
        asPage: PUBLIC_PATHS.includes(pathname),
        initialData: data,
      });
    },
    onError: (e) => {
      const errorData = e?.response?.data;
      toast.error({
        title: errorData?.title,
        message: errorData?.description,
        withIcon: true,
      });
    },
  });

  const requestResetPasscode = useCallback(
    (mobile: string) => {
      forgotPassCode({ mobile });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phoneNumber]
  );

  const handleCloseUserNotFoundModal = () => {
    closeAuthModal('userNotFound');
    setPhoneNumber(null);
    setApiError(null);
  };

  const handleUserNotFoundConfirm = () => {
    handleCloseUserNotFoundModal();

    const queryParams = new URLSearchParams(window.location.search);
    const phoneNumberParam = modals.userNotFound.mobile;

    if (pathname === AuthTab.SignIn.toString() && phoneNumberParam) {
      queryParams.set('phoneNumber', encodeURIComponent(phoneNumberParam));

      const currentParams = new URLSearchParams(window.location.search);
      const redirectUri = currentParams.get('redirect_uri');
      if (redirectUri) {
        queryParams.set('redirect_uri', encodeURIComponent(redirectUri));
      }

      const newUrl = `${ROUTES.signUp}?${queryParams.toString()}`;
      navigate(newUrl);
    } else {
      setActiveAuthStep(AuthStep.PhoneNumber);
      setAuthTabScreen(AuthTab.SignUp);
    }
  };

  return (
    <AuthModalContext.Provider value={{ openAuthModal, closeAuthModal }}>
      {children}

      {modals.banned.opened ? (
        <BannedModal
          {...modals.banned}
          lang={lang}
          onClose={() => closeAuthModal('banned')}
          onConfirm={
            modals.banned.type === AuthStep.PassCode && phoneNumber
              ? () => requestResetPasscode(phoneNumber)
              : () => {
                  setActiveAuthStep(AuthStep.PhoneNumber);
                  setApiError(null);
                }
          }
        />
      ) : null}

      {modals.confirmResetPasscode.opened &&
      modals.confirmResetPasscode.mobile ? (
        <ConfirmResetPasscodeModal
          lang={lang}
          mobile={modals.confirmResetPasscode.mobile}
          {...modals.confirmResetPasscode}
          onClose={() => closeAuthModal('confirmResetPasscode')}
          onConfirm={requestResetPasscode}
        />
      ) : null}

      {modals.forgotPasscode.opened && modals.forgotPasscode.mobile ? (
        <ForgotPasscodeModal
          lang={lang}
          mobile={modals.forgotPasscode.mobile}
          {...modals.forgotPasscode}
          onClose={() => closeAuthModal('forgotPasscode')}
          isMobile={isMobile}
        />
      ) : null}

      {modals.userNotFound.opened && modals.userNotFound.mobile ? (
        <UserNotFoundModal
          lang={lang}
          phoneNumber={modals.userNotFound.mobile}
          {...modals.userNotFound}
          onClose={handleCloseUserNotFoundModal}
          onConfirm={handleUserNotFoundConfirm}
        />
      ) : null}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
